// @flow
import React, { Component, Fragment } from 'react'
import ImagePreview from '../ImagePreview'
import Modal from 'react-responsive-modal'
import styled from 'styled-components'
import { grayColor, grayTextColor } from '../controls/colors'
import Cropper from 'react-cropper'
import FlatButton from '../controls/FlatButton'
import getDefaultCrop from '../getDefaultCrop'
import _ from 'lodash'
import type { Crop, Image } from '../types'
import { translateFunc, translations } from '../../shared/localize'

const Wrapper = styled.div`
  display: inline-block;
  border-radius: 3px;
  border: 1px solid ${grayColor};
  position: relative;
`
const Edit = ((styled.i: any).attrs({ className: 'glyphicon glyphicon-pencil' }): any)`
  &.glyphicon-pencil {
    position: absolute;
    top: 0;
    right: 0;
    background-color: white;
    color: ${grayTextColor};
    padding: 3px 3px 2px 4px;
    border-radius: 0 0 0 3px;
    cursor: pointer;
    border: 1px solid ${grayColor};
    border-top: none;
    border-right: none;
  }
`
const ButtonWrapper = styled.div`
  display: inline-block;
  margin: 10px 5px 0 0;
`
const Comment = styled.div`
  font-size: 14px;
  line-height: 100%;
  padding-bottom: 8px;
`

type Props = {|
  image: Image,
  ratio: number,
  minWidth: number,
  minHeight: number,
  crop: Crop,
  height?: number,
  onChange: Function
|}

type State = {|
  isOpen: boolean
|}

const t = translateFunc(translations.ad_edit.image_cropper)

export default class SingleImageCropper extends Component<Props, State> {
  prevData: Crop
  cropper: any

  constructor(props: Props) {
    super(props)

    this.prevData = this.pluginCrop()
    this.state = { isOpen: false }
  }

  correct() {
    const { minWidth, minHeight, ratio } = this.props
    const prevData = this.prevData

    var data = this.cropper.getData()

    let { x, y, width, height } = data
    if (width < minWidth || height < minHeight) {
      let chagesRatio = 0

      const dw = prevData.width - width
      const adw = prevData.width - minWidth
      const dh = prevData.height - height
      const adh = prevData.height - minHeight
      const dx = prevData.x - x
      const dy = prevData.y - y

      chagesRatio = Math.max(dw === 0 ? 0 : adw / dw, dh === 0 ? 0 : adh / dh)

      x = Math.round(prevData.x + dx * chagesRatio)
      y = Math.round(prevData.y + dy * chagesRatio)
      if (width < minWidth) {
        width = minWidth
        height = Math.floor(width / ratio)
      }
      if (height < minHeight) {
        height = minHeight
        width = Math.floor(height * ratio)
      }

      if (width < minWidth) width = minWidth
      if (height < minHeight) height = minHeight

      data = { x, y, width, height }

      if (prevData.width === minWidth || prevData.height === minHeight) {
        data = prevData
      }

      this.cropper.setData(_.cloneDeep(data))
    }
    this.prevData = data
  }

  render() {
    const { image, height, ratio } = this.props
    const { isOpen } = this.state

    return (
      <Fragment>
        <Wrapper>
          <ImagePreview
            imageSrc={image.src}
            size={{ width: image.width, height: image.height }}
            crop={this.crop()}
            height={height || 100}
            float
          />
          <Edit onClick={() => this.open()} />
          <Modal open={isOpen} onClose={() => this.close()}>
            <Comment>
              <span>{t('make_crop')}</span>
            </Comment>
            <Cropper
              ref={(el) => {
                this.cropper = el
              }}
              src={image.src}
              style={{ width: 600, height: 400 }}
              // Cropper.js options
              aspectRatio={ratio}
              guides={false}
              center={false}
              autoCropArea={1.0}
              rotatable={false}
              zoomable={false}
              movable={false}
              scalable={false}
              viewMode={1}
              toggleDragModeOnDblclick={false}
              data={this.pluginCrop()}
              crop={() => this.correct()}
            />
            <ButtonWrapper>
              <FlatButton primary onClick={() => this.applyCrop()}>
                {t('ok')}
              </FlatButton>
            </ButtonWrapper>
            <ButtonWrapper>
              <FlatButton onClick={() => this.close()}>{t('cancel')}</FlatButton>
            </ButtonWrapper>
          </Modal>
        </Wrapper>
      </Fragment>
    )
  }

  applyCrop() {
    const { onChange } = this.props
    const data = this.cropper.getData()
    const crop: Crop = {
      x: Math.max(0, Math.floor(data.x)),
      y: Math.max(0, Math.floor(data.y)),
      width: Math.round(data.width),
      height: Math.round(data.height)
    }

    onChange(crop)
    this.setState({ isOpen: false })
  }

  open() {
    this.setState({ isOpen: true })
  }

  close() {
    this.setState({ isOpen: false })
  }

  crop() {
    return this.props.crop || this.defaultCrop()
  }

  pluginCrop() {
    const { x, y, width, height } = this.crop()

    return { x, y, width, height }
  }

  defaultCrop() {
    const { image, ratio } = this.props

    return getDefaultCrop(image.width, image.height, ratio)
  }
}
